import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Tooltip } from 'antd';
import IconButton from '@mui/material/IconButton';
import { RcFile } from 'antd/lib/upload';
import clsx from 'clsx';

import { Credit, CreditCrop, CreditCropDocument, CxCredit } from '@types';
import { FilePreview, FileUpload } from '@components';
import { s3http } from '@network';
import { dateFormatter, rbac } from '@utils';
import { useOutsideClick } from '@hooks';

interface CropDocumentsProps {
  policyIdx: number;
  documents: CreditCropDocument[];
  insurance: CreditCrop;
  updateInsurance: (ins: CreditCrop) => void;
  credit: Credit;
  disabled?: boolean;
}

interface CropDocumentProps {
  policyIdx: number;
  insurance: CreditCrop;
  documentNumber: number;
  document: CreditCropDocument;
  updateInsurance: (ins: CreditCrop) => void;
  credit: Credit;
  openedIncident: number | undefined;
  setOpenedIncident: (openedIncident: number | undefined) => void;
  disabled?: boolean;
}

export const CropDocuments = (props: CropDocumentsProps) => {
  const { documents, policyIdx, insurance, updateInsurance, credit, disabled } = props;
  const [openedIncident, setOpenedIncident] = useState<number>();
  const cls = useStyles();
  const admin = rbac.getAdmin();

  const uploadInsuranceFile = async (rcFile: RcFile): Promise<string> => {
    const file = await s3http.uploadFile(rcFile as File);
    const newDocument: CreditCropDocument = {
      file,
      fileId: file.id,
      url: file.url,
      adminName: admin?.name,
    };
    const nextDocuments = [...insurance.creditCropDocuments ?? [], newDocument];
    updateInsurance({ ...insurance, creditCropDocuments: nextDocuments });
    return file.adminUrl;
  };

  return (
    <div className={cls.documentsBox}>
      <div className={cls.documentTitle}>Policy #{policyIdx + 1} files</div>
      <div className={cls.documents}>
        {documents.map((document, documentNumber) => (
          <CropDocument
            key={document.fileId}
            policyIdx={policyIdx}
            document={document}
            documentNumber={documentNumber}
            insurance={insurance}
            updateInsurance={updateInsurance}
            credit={credit}
            openedIncident={openedIncident}
            setOpenedIncident={setOpenedIncident}
            disabled={disabled}
          />
        ))}
        {disabled ? null : (
          <div className={cls.document}>
            <div className={cls.documentName}>Add File</div>
            <FileUpload
              uploadAction={uploadInsuranceFile}
              type="pdf"
              className={cls.upload} />
          </div>
        )}
      </div>
    </div>
  );
};

const CropDocument = (props: CropDocumentProps) => {
  const {
    document, documentNumber, policyIdx, insurance, updateInsurance,
    credit, openedIncident, setOpenedIncident, disabled,
  } = props;
  const cls = useStyles();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isTooltipOpened = openedIncident === documentNumber;

  useEffect(() => {
    const ref = tooltipRef;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (ref.current
        && !ref.current.contains(event.target as Node)
        && !ref.current.isEqualNode(event.target as Node)
      ) {
        if (isTooltipOpened) setOpenedIncident(undefined);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('touchstart', handleClickOutside);

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('touchstart', handleClickOutside);
    };
  }, [tooltipRef, setOpenedIncident, isTooltipOpened]);

  const removeDocument = () => {
    const nextDocuments = (insurance.creditCropDocuments ?? [])
      .filter((_, i) => i !== documentNumber);
    updateInsurance({ ...insurance, creditCropDocuments: nextDocuments });
  };

  const reportIncident = () => {
    const nextOpened = openedIncident === documentNumber ? undefined : documentNumber;
    setOpenedIncident(nextOpened);
    if (nextOpened !== undefined) {
      const focus = () => {
        if (!textareaRef.current) return;
        textareaRef.current.selectionStart = 10000;
        textareaRef.current.selectionEnd = 10000;
        textareaRef.current.focus();
      };
      setTimeout(focus, 100);
      setTimeout(focus, 500);
      setTimeout(focus, 1000);
    }
  };

  const updateReport = (idx: number, reportIncident: string) => {
    const nextDocuments = (insurance.creditCropDocuments ?? []).map((document, i) => {
      if (i !== idx) return document;
      document.reportIncident = reportIncident;
      return document;
    });
    updateInsurance({ ...insurance, creditCropDocuments: nextDocuments });
  };

  const preventClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  return (
    <div className={cls.document} key={document.fileId}>
      <div className={cls.documentName}>File #{documentNumber + 1}</div>
      {document.reportIncident ? (
        <IconButton
          title="Incident Reported"
          onClick={reportIncident}
          className={clsx(cls.btn, cls.btnIncident)}
          disabled={disabled}
        ><DangerIcon /></IconButton>
      ) : (
        disabled ? null : <IconButton
          className={clsx(cls.btn, cls.btnRemove)}
          onClick={removeDocument}
        ><DeleteIcon /></IconButton>
      )}
      <FilePreview
        file={document.file}
        wrapClass={cls.preview}
        downloadFilename={
          `Insurance ${credit.code} Policy#${policyIdx + 1} File#${documentNumber + 1}`
        }
      />
      {disabled ? null : (
        <IconButton
          className={clsx(
            cls.btn,
            cls.btnReport,
            document.reportIncident && cls.btnReported,
          )}
          onClick={reportIncident}
        >
          <Tooltip
            overlayClassName={cls.report}
            open={isTooltipOpened}
            title={(
              <div className={cls.reportBox} onClick={preventClick} ref={tooltipRef}>
                <div className={cls.reportHeader}>
                  <div>Situation Report</div>
                  <div className={cls.reportName}>
                    {document.adminName}
                    &nbsp;|&nbsp;
                    {dateFormatter.toDateSlash(document.file.createdAt, true)}
                  </div>
                </div>
                <textarea
                  autoFocus
                  onClick={preventClick}
                  className={cls.reportText}
                  placeholder="The situation reported"
                  value={document.reportIncident || ''}
                  onChange={e => updateReport(documentNumber, e.target.value)}
                  maxLength={100}
                  ref={textareaRef}
                />
              </div>
            )}
          >{document.reportIncident ? 'Incident Reported' : 'Report Incident'}</Tooltip>
        </IconButton>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  report: {
    marginLeft: '-62px !important',
    '&, & .ant-tooltip-content, & .ant-tooltip-inner': {
      width: 370,
      height: 100,
      backgroundColor: '#FFF',
    },
    '& .ant-tooltip-arrow-content': {
      '--antd-arrow-background-color': 'unset',
      '&::before': {
        backgroundColor: '#FFF !important',
      },
    },
  },
  reportBox: {
    color: '#000',
  },
  reportHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    marginBottom: 10,
  },
  reportText: {
    backgroundColor: '#EFEFEF',
    fontSize: 14,
    lineHeight: 1.1,
    width: '100%',
    height: 65,
    borderRadius: 5,
    padding: '5px 10px',
  },
  reportName: {
    fontSize: 12,
    fontWeight: 300,
  },
  documentsBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  documents: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  document: {
    marginRight: 15,
    marginBottom: 15,
    position: 'relative',
  },
  documentName: {
    fontSize: 12,
    lineHeight: 1,
    color: 'var(--text-color)',
    textAlign: 'center',
    marginBottom: 3,
    marginLeft: -5,
  },
  documentTitle: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1,
    color: 'var(--title-color)',
    marginBottom: 4,
    textAlign: 'center',
    paddingBottom: 10,
  },
  btn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#042E6B',
    color: '#FFF',
    fontSize: 12,
    borderRadius: 4,
    opacity: 0.8,
    '&:hover': {
      opacity: 1,
      boxShadow: '0 4px 8px 0 rgba(48, 55, 61, 0.2)',
    },
  },
  btnRemove: {
    position: 'absolute',
    zIndex: 5,
    top: 15,
    right: 0,
    backgroundColor: 'transparent !important',
  },
  btnIncident: {
    position: 'absolute',
    zIndex: 5,
    top: -11,
    left: 0,
    opacity: 1,
    backgroundColor: 'transparent !important',
    paddingBottom: '3px !important',
  },
  btnReport: {
    position: 'absolute',
    zIndex: 5,
    height: 26,
    top: 'auto',
    bottom: 2,
    left: 2,
    right: 2,
    borderRadius: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    opacity: 0.8,
    '&:hover': {
      opacity: 1,
      boxShadow: '0 4px 8px 0 rgba(48, 55, 61, 0.2)',
      backgroundColor: '#042E6B !important',
      color: '#FFF !important',
    },
  },
  btnReported: {
    backgroundColor: '#ff5e00 !important',
    opacity: 0.8,
    '&:hover': {
      opacity: 1,
      boxShadow: '0 4px 8px 0 rgba(48, 55, 61, 0.2)',
      backgroundColor: '#ff5e00 !important',
      color: '#FFF !important',
    },
  },
  upload: {
    width: 128,
    height: 164,
  },
  preview: {
    width: 128,
    height: 164,
    border: '2px solid #042E6B',
    borderRadius: '10px',
    background: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
});

const DeleteIcon = () => (
  <svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.32511 2.40625C8.35111 2.51427 8.46113 2.60028 8.57314 2.60028H10.7034C11.0895 2.60028 11.4035 2.91432 11.4035 3.30037C11.4035 3.62042 11.1895 3.88845 10.8975 3.97246L10.5314 14.0258C10.5014 14.8439 9.82131 15.5 9.0012 15.5H2.40232C1.58421 15.5 0.90212 14.8419 0.872116 14.0258L0.506067 3.97246C0.214029 3.88845 0 3.61842 0 3.30037C0 2.91432 0.314042 2.60028 0.700093 2.60028H2.83038C2.94039 2.60028 3.05041 2.51227 3.07841 2.40625L3.22843 1.80617C3.41445 1.06608 4.13855 0.5 4.90065 0.5H6.50287C7.26497 0.5 7.99107 1.06608 8.17509 1.80617L8.32511 2.40625ZM5.20069 5.80071V12.6016C5.20069 12.8777 5.42472 13.1017 5.70076 13.1017C5.9768 13.1017 6.20083 12.8777 6.20083 12.6016V5.80071C6.20083 5.52467 5.9768 5.30064 5.70076 5.30064C5.42472 5.30064 5.20069 5.52467 5.20069 5.80071ZM2.90039 5.81471L3.10041 12.6156C3.10841 12.8917 3.33844 13.1097 3.61448 13.1017C3.89052 13.0937 4.10855 12.8636 4.10055 12.5876L3.90052 5.78671C3.89252 5.51067 3.66249 5.29264 3.38645 5.30064C3.11041 5.30864 2.89239 5.53867 2.90039 5.81471ZM7.501 5.7847L7.30097 12.5856C7.29297 12.8616 7.511 13.0917 7.78704 13.0997C8.06308 13.1077 8.29311 12.8897 8.30111 12.6136L8.50113 5.81271C8.50913 5.53667 8.29111 5.30664 8.01507 5.29864C7.73903 5.29064 7.509 5.50867 7.501 5.7847ZM4.57061 2.59828H6.82891C6.88292 2.59828 6.91692 2.55427 6.90492 2.50227L6.81491 2.14422C6.7849 2.0262 6.62088 1.89819 6.50087 1.89819H4.89865C4.77864 1.89819 4.61461 2.0262 4.58461 2.14422L4.4946 2.50227C4.4806 2.55627 4.5146 2.59828 4.57061 2.59828Z"
      fill="#FF0000" />
  </svg>
);

const DangerIcon = () => (
  <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.33311 1C7.10291 -0.333333 9.02741 -0.333334 9.79721 0.999999L15.8594 11.5C16.6292 12.8333 15.6669 14.5 14.1273 14.5H2.00299C0.463384 14.5 -0.498868 12.8333 0.270933 11.5L6.33311 1Z"
      fill="#FF0000" />
    <path d="M7.25516 9.752L7.04516 2.92H8.65516L8.44516 9.752H7.25516ZM7.84316 13.14C7.5445 13.14 7.29716 13.042 7.10116 12.846C6.91449 12.6407 6.82116 12.3933 6.82116 12.104C6.82116 11.8053 6.91449 11.558 7.10116 11.362C7.29716 11.166 7.5445 11.068 7.84316 11.068C8.14183 11.068 8.38449 11.166 8.57116 11.362C8.76716 11.558 8.86516 11.8053 8.86516 12.104C8.86516 12.3933 8.76716 12.6407 8.57116 12.846C8.38449 13.042 8.14183 13.14 7.84316 13.14Z"
      fill="white" />
  </svg>
);
